"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";

/**
 * Eligibility 5012 — KUK eligibility criteria.
 *
 * Port of kuk-campus-portal `components/Landing/Eligibility.tsx`: a heading above
 * a single tinted panel that carries the entry requirement and, optionally, a
 * note under it.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * TWO LINES, NOT AN ARRAY. v1 receives `eligibility: [criteria, note]` — a
 * two-element array built from two separate database columns and then indexed by
 * position, so a null first element hides the whole section even when the note
 * is set. Here they are two named fields: the criteria and an optional note. The
 * section renders whenever either is present.
 *
 * COLUMN MAPPING — `eligibility` has one description column, so the note is
 * stored in `read_more_text` / `read_more_color`. Those names are historical;
 * the admin form labels them for what they do here. Flagged so nobody wires a
 * "read more" link to them later expecting a link.
 *
 * DROPPED — v1's `space-y-10` between the two lines, which opens a 40px gap
 * inside a panel whose own padding is 20px. The reference ships almost every
 * page with an empty note, so the gap is invisible there and reads as a layout
 * bug on the pages that do use it; the spacing here is proportionate to the
 * panel.
 *
 * DROPPED — v1's percentage widths (`w-[7%]` on the icon, `w-[90%]` on the
 * text). A percentage-width icon changes size with the viewport and squashes
 * below ~360px. The icon is a fixed 18px, as it renders at v1's own default
 * width, and the text simply takes the remaining space.
 */

/** v1: `width={18} height={18}` on the leading icon. */
const ICON_SIZE = "18";

/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used
 * on 12 headings across the reference. Defined in ITS globals.css as a `::after`
 * with `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can
 * come from a CMS field (a pseudo-element cannot read an inline style), and so
 * the classes are ones Tailwind certainly emits — an arbitrary `after:` variant
 * would need verifying against the built CSS.
 *
 * `w-fit` on the heading is load-bearing: the rule is 33% of the HEADING, and a
 * full-width h2 would stretch it across a third of the page.
 */
const HEADING_UNDERLINE =
  "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

/**
 * v1's leading glyph is `/icons/user.svg`, a file in the KUK app's own public
 * directory — not a CDN URL and not an entry in this app's icon map, so neither
 * a copied URL nor the name "user" resolves here (the name falls through to
 * `DynamicIcon`'s no-image placeholder, which is the broken-image mark).
 *
 * Carried over as inline SVG, matching how the header's brand marks are handled.
 * `button_icon` stays an override: an icon-map key or an uploaded URL wins.
 */
const EligibilityMark = ({ size, color }: { size: string; color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M9.99984 9.99996C8.7165 9.99996 7.61789 9.54301 6.704 8.62913C5.79012 7.71524 5.33317 6.61663 5.33317 5.33329C5.33317 4.04996 5.79012 2.95135 6.704 2.03746C7.61789 1.12357 8.7165 0.666626 9.99984 0.666626C11.2832 0.666626 12.3818 1.12357 13.2957 2.03746C14.2096 2.95135 14.6665 4.04996 14.6665 5.33329C14.6665 6.61663 14.2096 7.71524 13.2957 8.62913C12.3818 9.54301 11.2832 9.99996 9.99984 9.99996ZM0.666504 19.3333V16.0666C0.666504 15.4055 0.836837 14.7981 1.1775 14.2443C1.51817 13.6905 1.97006 13.2674 2.53317 12.975C3.73873 12.3722 4.96373 11.9203 6.20817 11.6193C7.45262 11.3183 8.7165 11.1674 9.99984 11.1666C11.2832 11.1658 12.5471 11.3167 13.7915 11.6193C15.0359 11.9218 16.2609 12.3737 17.4665 12.975C18.0304 13.2666 18.4827 13.6897 18.8233 14.2443C19.164 14.7988 19.334 15.4063 19.3332 16.0666V19.3333H0.666504Z"
      fill={color}
    />
  </svg>
);

/** Only an uploaded URL or a real icon-map key overrides the mark above. */
const isIconOverride = (v: string) => v.trim().length > 0 && v.trim() !== "user";

interface Eligibility5012Data {
  status?: boolean | number | string;
  title?: string;
  title_text_color?: string;
  /** The entry requirement. */
  description?: string;
  description_text_color?: string;
  /** Optional note under it. Stored in `read_more_text` — see COLUMN MAPPING. */
  read_more_text?: string;
  read_more_color?: string;
  /** Panel fill. */
  background_color?: string;
  /** Leading glyph — an `iconMap` key or an uploaded icon URL. */
  button_icon?: string;
  button_icon_alt?: string;
  button_color?: string;
  /** Panel border — v1's Card border (slate-200). */
  category_border_color?: string;
}

const Eligibility5012 = (props: { data?: Eligibility5012Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | Eligibility5012Data
    | undefined;

  const {
    title = "",
    title_text_color = "#101112",
    description = "",
    description_text_color = "#101112",
    read_more_text = "",
    read_more_color = "#4B4B4B",
    background_color = "rgba(159, 28, 51, 0.10)",
    button_icon = "",
    button_icon_alt = "",
    button_color = "#9F1C33",
    category_border_color = "#E2E8F0",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  // v1 gates on the first array element; here either line is enough to render.
  if (!status || (!description && !read_more_text)) return null;

  return (
    <section className="flex w-full flex-col items-center justify-center px-5 py-10 tab:pt-10">
      <div className="flex w-full max-w-[1120px] flex-col items-start justify-between gap-10 p-5 tab:px-20 tab:py-10">
        {title ? (
          <h2
            className="relative w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_text_color }}
          >
            {title}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: button_color }}
            />
          </h2>
        ) : null}

        {/*
          v1's panel is the reference's shadcn `Card`, so it carries that
          component's chrome — `rounded-xl border border-slate-200 shadow` — with
          only the fill overridden. Reproduced; a flat block reads noticeably
          different against the page.

          The icon and copy take v1's proportional columns (7%/3% and 90%/99%)
          rather than a gap, which is how the reference spaces them; `shrink-0`
          keeps the glyph at its intrinsic size when the column is narrow.
        */}
        <div
          className="mx-auto flex w-full items-start justify-between rounded-xl border p-5 shadow"
          style={{
            backgroundColor: background_color,
            borderColor: category_border_color,
          }}
        >
          <span className="w-[7%] shrink-0 py-1 tab:w-[3%]">
            {isIconOverride(button_icon) ? (
              <DynamicIcon
                iconName={button_icon}
                width={ICON_SIZE}
                height={ICON_SIZE}
                color={button_color}
              />
            ) : (
              <EligibilityMark size={ICON_SIZE} color={button_color} />
            )}
          </span>

          <div className="flex w-[90%] min-w-0 flex-col gap-3 tab:w-[99%]">
            {description ? (
              <p
                className="text-base font-light leading-7 tracking-wide"
                style={{ color: description_text_color }}
              >
                {description}
              </p>
            ) : null}
            {read_more_text ? (
              <p
                className="text-sm font-light leading-7 tracking-wide tab:text-base"
                style={{ color: read_more_color }}
              >
                {read_more_text}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {/* Alt text for an uploaded icon; icon-map glyphs are decorative. */}
      {button_icon_alt ? (
        <span className="sr-only">{button_icon_alt}</span>
      ) : null}
    </section>
  );
};

export default Eligibility5012;
